import React, { useState } from "react";
import { Search, Edit, Trash2, Plus, X, Save, Loader2, AlertTriangle, Swords } from "lucide-react";
import useConcurrents from "../../hooks/concurrents/useConcurrents";
import { Concurrent } from "../../utils/database";

interface FilterState {
  search: string;
}

// Modal de confirmation de suppression
const DeleteConfirmModal: React.FC<{
  concurrent: Concurrent | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}> = ({ concurrent, isOpen, onClose, onConfirm, isLoading }) => {
  if (!isOpen || !concurrent) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800">Confirmer la suppression</h2>
        </div>

        <div className="mb-6">
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer le concurrent <strong>{concurrent.name}</strong> ?
          </p>
          <p className="text-sm text-gray-500 mt-2">Cette action est irréversible.</p>
        </div>

        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isLoading}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Suppression...</span>
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                <span>Supprimer</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal d'édition / création
const EditConcurrentModal: React.FC<{
  concurrent: Concurrent | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (concurrent: Concurrent) => void;
  isLoading: boolean;
  isCreating: boolean;
}> = ({ concurrent, isOpen, onClose, onSave, isLoading, isCreating }) => {
  const [form, setForm] = useState({ name: "", taxe: "" });
  const [errors, setErrors] = useState<{ name?: string; taxe?: string }>({});

  React.useEffect(() => {
    if (isCreating) {
      setForm({ name: "", taxe: "" });
    } else if (concurrent) {
      setForm({ name: concurrent.name, taxe: concurrent.taxe });
    }
    setErrors({});
  }, [concurrent, isCreating, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: { name?: string; taxe?: string } = {};
    if (!form.name.trim()) {
      newErrors.name = "Le nom est requis";
    }
    if (!form.taxe.trim()) {
      newErrors.taxe = "La taxe est requise";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const data: Concurrent = isCreating
        ? { name: form.name, taxe: form.taxe }
        : { id: concurrent?.id, name: form.name, taxe: form.taxe };
      onSave(data);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">
            {isCreating ? "Ajouter un Concurrent" : "Modifier le Concurrent"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom du Concurrent *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, name: e.target.value }));
                if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                errors.name ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="Ex: Western Union, MoneyGram..."
              disabled={isLoading}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Taxe *</label>
            <input
              type="text"
              value={form.taxe}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, taxe: e.target.value }));
                if (errors.taxe) setErrors((prev) => ({ ...prev, taxe: undefined }));
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                errors.taxe ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="Ex: 5%, 10 CAD..."
              disabled={isLoading}
            />
            {errors.taxe && <p className="mt-1 text-sm text-red-600">{errors.taxe}</p>}
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{isCreating ? "Création..." : "Modification..."}</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{isCreating ? "Créer" : "Modifier"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/** Panneau concurrents (sans Layout) — utilisé dans Paramètres. */
export const ConcurrentsPanel: React.FC = () => {
  const { concurrents, deleteConcurrent, updateConcurrent, createConcurrent, concurrentsLoading } = useConcurrents({});

  const [filters, setFilters] = useState<FilterState>({ search: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [editingConcurrent, setEditingConcurrent] = useState<Concurrent | null>(null);
  const [deletingConcurrent, setDeletingConcurrent] = useState<Concurrent | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const itemsPerPage = 10;

  const filteredConcurrents = (concurrents || []).filter((c) => {
    return (
      filters.search === "" ||
      c.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      c.taxe.toLowerCase().includes(filters.search.toLowerCase())
    );
  });

  const totalPages = Math.ceil(filteredConcurrents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredConcurrents.length);
  const currentConcurrents = filteredConcurrents.slice(startIndex, endIndex);

  const handleAction = (action: string, concurrent: Concurrent) => {
    switch (action) {
      case "edit":
        setIsCreating(false);
        setEditingConcurrent(concurrent);
        break;
      case "delete":
        setDeletingConcurrent(concurrent);
        break;
    }
  };

  const handleSave = async (concurrent: Concurrent) => {
    setIsEditLoading(true);
    try {
      if (isCreating) {
        await createConcurrent(concurrent);
      } else {
        await updateConcurrent(concurrent);
      }
      setEditingConcurrent(null);
      setIsCreating(false);
    } catch (error) {
      console.error("Erreur:", error);
      alert(isCreating ? "Erreur lors de la création" : "Erreur lors de la modification");
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingConcurrent) return;
    setIsDeleteLoading(true);
    try {
      await deleteConcurrent(deletingConcurrent.id);
      setDeletingConcurrent(null);
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      alert("Erreur lors de la suppression du concurrent");
    } finally {
      setIsDeleteLoading(false);
    }
  };

  if (concurrentsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des concurrents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Swords className="w-5 h-5 text-green-600" />
              Gestion des Concurrents
            </h2>
            <p className="text-sm text-gray-600 mt-1">Gestion des concurrents et de leurs tarifs</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold text-green-600">{concurrents?.length || 0}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par nom, taxe..."
                value={filters.search}
                onChange={(e) => {
                  setFilters({ search: e.target.value });
                  setCurrentPage(1);
                }}
                className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
          <button
            onClick={() => {
              setIsCreating(true);
              setEditingConcurrent({ name: "", taxe: "" });
            }}
            className="ml-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter Concurrent</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Nom</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Taxe</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentConcurrents.length > 0 ? (
                currentConcurrents.map((concurrent) => (
                  <tr key={concurrent.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-800">{concurrent.name}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium">{concurrent.taxe}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleAction("edit", concurrent)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleAction("delete", concurrent)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="px-6 py-8 text-center text-gray-500">
                    Aucun concurrent trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Affichage {startIndex + 1}-{endIndex} sur {filteredConcurrents.length} concurrents
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Précédent
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} sur {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Suivant
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <EditConcurrentModal
        concurrent={editingConcurrent}
        isOpen={!!editingConcurrent}
        onClose={() => {
          setEditingConcurrent(null);
          setIsCreating(false);
        }}
        onSave={handleSave}
        isLoading={isEditLoading}
        isCreating={isCreating}
      />

      <DeleteConfirmModal
        concurrent={deletingConcurrent}
        isOpen={!!deletingConcurrent}
        onClose={() => setDeletingConcurrent(null)}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleteLoading}
      />
    </div>
  );
};

export default ConcurrentsPanel;
